import React, { ChangeEvent, useState } from "react";
import {
  Modal,
  Button,
  Text,
  Card,
  Checkbox,
  Icon,
  Flex,
  ErrorMessage,
  toast,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useDeleteDashboardMutation } from "../../../graphql/types/gqlSchemaTypes";

interface ConfirmDeleteModalProps {
  showModal: boolean;
  dashboardName: string;
  dashboardId: string | number;
  arenaId: string | number;
  deleteConfirmationChecked: boolean;
  confirmDeleteError: string | null;
  onCheckboxChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onHide: () => void;
  onDeleted?: () => void;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  showModal,
  dashboardName,
  dashboardId,
  arenaId,
  deleteConfirmationChecked,
  confirmDeleteError: initialConfirmDeleteError,
  onCheckboxChange,
  onHide,
  onDeleted,
}) => {
  const [confirmDeleteError, setConfirmDeleteError] = useState<string | null>(
    initialConfirmDeleteError,
  );
  const confirmButtonText = `Delete ${dashboardName}`;
  const checkboxText = `I acknowledge I want to delete ${dashboardName}`;
  const cardStyle = confirmDeleteError ? { borderColor: Colors.Danger600 } : {};

  const [executeDeleteDashboardMutation] = useDeleteDashboardMutation();

  const deleteDashboard = () => {
    if (!dashboardId) {
      throw new Error("Failed to delete dashboard: No dashboard ID provided");
    }
    if (!deleteConfirmationChecked) {
      setConfirmDeleteError("You must acknowledge before deleting");
      return;
    }
    void executeDeleteDashboardMutation({
      variables: {
        objectInput: {
          id: typeof dashboardId === "number" ? dashboardId : parseInt(dashboardId, 10),
          arenaId: typeof arenaId === "number" ? arenaId : parseInt(arenaId, 10),
        },
      },
      onError: () => {
        toast.error(`Failed to delete dashboard`);
      },
      onCompleted: () => {
        toast.success("Dashboard deleted");
        onDeleted?.();
        handleHide();
      },
    });
  };

  const handleHide = () => {
    setConfirmDeleteError(null);
    onHide();
  };

  return (
    <Modal size="small" show={showModal} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <Flex justifyContent="center" flexGrow={1}>
            <Icon
              type="exclamation-triangle"
              variant="outline"
              size="xxx-large"
              color={Colors.Neutral600}
            />
          </Flex>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Text align="center" variant="h2" marginBottom="sm">
          {dashboardName} will be deleted
        </Text>
        <Text variant="secondary-sm" align="center" marginBottom="md">
          If this dashboard is shared, others in your workspace will no longer have access to it
        </Text>
        <Card style={cardStyle} padding="md">
          <Checkbox
            onChange={onCheckboxChange}
            checked={deleteConfirmationChecked}
            label={checkboxText}
          />
        </Card>
        {confirmDeleteError && <ErrorMessage>{confirmDeleteError}</ErrorMessage>}
      </Modal.Body>
      <Modal.Footer>
        <Text marginBottom="sm">
          <Button onClick={deleteDashboard} variant="danger" isFullWidth className="stacked-sm">
            {confirmButtonText}
          </Button>
        </Text>
        <Text>
          <Button onClick={handleHide} variant="neutral" isFullWidth>
            Cancel
          </Button>
        </Text>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDeleteModal;
